import styled from '@xstyled/styled-components';
import React from 'react';
import { shallow } from 'zustand/shallow';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useMediaPlayerStore } from './MediaPlayerProvider';
import { MediaPlayerStore } from './MediaPlayerStore';
import { useCanControl } from '~/ui/video-player/useCanControl';

/**
 * Figma EDS-CAT 315:3348 (light) / 421:24330 (dark).
 * Anchored 8px from the bottom; grows upward when text wraps.
 * Horizontal inset 62px keeps captions clear of corner HUD / nav buttons.
 */
const CaptionContainer = styled.div`
  position: fixed;
  pointer-events: none;
  bottom: 8px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 0 62px;
  box-sizing: border-box;
  max-height: calc(100% - 16px);
  overflow: hidden;
  z-index: 100;
`;

const Caption = styled.div`
  max-width: 100%;
  width: fit-content;
  padding: 8px 16px;
  box-sizing: border-box;
  background: #2e2e2e;
  color: #ffffff;
  border-radius: 4px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  font-variant-numeric: lining-nums proportional-nums;
  text-align: center;
  word-break: break-word;
  overflow-wrap: break-word;
  z-index: 100;
`;

const selector = (s: MediaPlayerStore) => select(s, 'captions', 'status');
const settingsSelector = (s: TrainingState) => select(s, 'subtitles');

export const MediaCaptions = () => {
  const { captions, status } = useMediaPlayerStore(selector, shallow);
  const { subtitles } = useTrainingStore(settingsSelector, shallow);

  const canControl = useCanControl();

  if (!captions.length || !subtitles) return null;

  if (status != 'playing' && status != 'paused') {
    return null;
  }

  return canControl ? (
    <CaptionContainer
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={t`Subtitles`}
    >
      {captions.map(c => (
        <Caption
          key={c}
          dangerouslySetInnerHTML={{ __html: c }}
          className="CaptionBox"
        />
      ))}
    </CaptionContainer>
  ) : null;
};
